import React, { useContext, useState, useEffect, useMemo } from "react";
import moment from "moment";
import { ALL_BOOKINGS_BY_PAGE_VIEW_QUERY } from "../../../graphql/Bookings/Bookings.graphql";
import { ModalContext } from "../../../components/ModalProvider";
import DataTable from "../../../components/DataTable";
import { COLUMNS } from "./columns";
import PageHeader from "../../../components/PageHeader";
import BookingHistoryFilters from "./BookingHistoryFilters";
import { getFilterState, saveFilterState } from "../../../utils";

import { AddBookingForm, EditBookingForm } from "../../../components/Bookings";
import { useCurrentUserRoles } from "../../../components/Authorization";

import { useCurrentUser } from "../../../utils/hooks";

import Loading from "../../../components/Loading";

const DEFAULT_FILTERS = {
  BookingCode_startsWith: "NA",
  PickupDate_gte:
    new Date().setHours(0, 0, 0, 0) - 1000 * 60 * 60 * 24 * 365 * 2
};

const BookingHistoryPage = () => {
  const INITIAL_FILTER_STATE = getFilterState(
    "BOOKING_HISTORY_FILTERS",
    DEFAULT_FILTERS
  );
  const { setModalOpen } = useContext(ModalContext);
  const [filters, setFilters] = useState({
    ...INITIAL_FILTER_STATE,
    ...DEFAULT_FILTERS
  });

  const { data: UserData } = useCurrentUser();

  const roles = useCurrentUserRoles();

  // if user is pto role only, lets only show them users they are supervisors for
  const shouldQueryBySupervisor = useMemo(
    () =>
      roles.some(role => role === "PTO") &&
      !roles.some(role => role === "ADMIN" || role === "HR"),
    [roles]
  );

  useEffect(() => {
    if (shouldQueryBySupervisor) {
      setFilters(prev => ({
        ...prev,
        SupervisorRecIdRef: UserData.EmployeeRecIdRef
      }));
    }
  }, [UserData, shouldQueryBySupervisor]);

  const handleSetFilters = inputs => {
    const where = { ...inputs };
    if (inputs.PickupDate_gte) {
      where.PickupDate_gte = moment(inputs.PickupDate_gte)
        .startOf("day")
        .toISOString();
    }
    if (inputs.PickupDate_lte) {
      where.PickupDate_lte = moment(inputs.PickupDate_lte)
        .endOf("day")
        .toISOString();
    }
    saveFilterState("BOOKING_HISTORY_FILTERS", where);
    setFilters({ ...where });
  };
  const resetFilters = () => handleSetFilters({ ...DEFAULT_FILTERS });

  return UserData?.RecId ? (
    <React.Fragment>
      <PageHeader
        FilterComponent={BookingHistoryFilters}
        columns={COLUMNS}
        filters={filters}
        name="VW_BookingsByPage"
        query={ALL_BOOKINGS_BY_PAGE_VIEW_QUERY}
        resetFilters={resetFilters}
        setFilters={handleSetFilters}
        title="Booking History"
        onAddClick={() =>
          setModalOpen(
            true,
            <AddBookingForm
              bookingCodeWhere={{ BookingCode_startsWith: "NA" }}
              refetchQueries="ALL_BOOKINGS_BY_PAGE_VIEW_QUERY"
              onComplete={() => setModalOpen(false, "")}
            />
          )
        }
      />

      <DataTable
        isPaginated
        columns={COLUMNS}
        dataQuery={ALL_BOOKINGS_BY_PAGE_VIEW_QUERY}
        initialSortBy={[
          { id: "PickupDate", desc: true },
          { id: "CreatedDateTime", desc: true }
        ]}
        name="VW_BookingsByPage"
        where={filters}
        onRowClick={row =>
          setModalOpen(
            true,
            <EditBookingForm
              bookingCodeWhere={{ BookingCode_startsWith: "NA" }}
              refetchQueries="ALL_BOOKINGS_BY_PAGE_VIEW_QUERY"
              state={{
                RecId: row.RecId,
                EmployeeRecIdRef: row.EmployeeRecIdRef,
                PickupDate: row.PickupDate,
                BookingDate: row.BookingDate,
                BookingCodeRecIdRef: row.BookingCodeRecIdRef,
                BookedTrip: row.BookedTrip,
                Comment: row.Comment,
                CreatedDateTime: row.CreatedDateTime,
                Reversed: row.Reversed
              }}
              onComplete={() => setModalOpen(false, "")}
            />
          )
        }
      />
    </React.Fragment>
  ) : (
    <Loading />
  );
};
BookingHistoryPage.propTypes = {};
export default BookingHistoryPage;
